/* eslint-disable jsx-a11y/media-has-caption */
import React from "react";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import DownloadIcon from "@mui/icons-material/Download";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { IgnoreZone } from "../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import { LineCrossing } from "../EditCamera/editCameraStore/editCameraStore";
import { AlertClipOverlays } from "./AlertClipOverlays";
import {
  formatPlayerTime,
  getDetectionMarkerRatio,
  getObjectContainRect,
} from "./AlertsUtils/alertClipOverlayGeometry";

// Long enough to tell a double-click (fullscreen) from a single click
// (play/pause), short enough that play/pause still feels immediate.
export const CLICK_TO_TOGGLE_PLAY_DELAY_MS = 250;

// Safari only ships the prefixed Fullscreen API.
type WebkitFullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => void;
};
type WebkitFullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => void;
};

const getFullscreenElement = (): Element | null => {
  const doc = document as WebkitFullscreenDocument;
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
};

const getViewportSize = () => ({
  width: window.innerWidth,
  height: window.innerHeight,
});

export const AlertClipPlayer = ({
  src,
  width,
  height,
  videoKey,
  approxCaptureTimestamp,
  clipStartTimestamp,
  ignoreZones,
  lineCrossings,
  showIgnoreZones,
  showLineCrossings,
  canDownload = false,
  onDownload,
}: {
  src: string;
  width: number;
  height: number;
  videoKey: string;
  approxCaptureTimestamp?: string | number | null;
  clipStartTimestamp?: string | number | null;
  ignoreZones: IgnoreZone[];
  lineCrossings: LineCrossing[];
  showIgnoreZones: boolean;
  showLineCrossings: boolean;
  // Download sits beside full screen in the control bar; it is disabled while
  // the clip is still partial or being checked.
  canDownload?: boolean;
  onDownload?: () => void;
}) => {
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const clickTimerRef = React.useRef<number | null>(null);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [viewportSize, setViewportSize] = React.useState(getViewportSize);
  const [isPlaying, setIsPlaying] = React.useState(true);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [mediaSize, setMediaSize] = React.useState({ width: 0, height: 0 });

  const syncFromVideo = () => {
    const video = videoRef.current;
    if (!video) return;
    setCurrentTime(video.currentTime || 0);
    setDuration(
      video.duration && Number.isFinite(video.duration) ? video.duration : 0
    );
    setIsPlaying(!video.paused);
    if (video.videoWidth && video.videoHeight) {
      setMediaSize({ width: video.videoWidth, height: video.videoHeight });
    }
  };

  const handleTogglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  };

  const clearClickTimer = () => {
    if (clickTimerRef.current !== null) {
      window.clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
  };

  const handleVideoClick = () => {
    clearClickTimer();
    clickTimerRef.current = window.setTimeout(() => {
      clickTimerRef.current = null;
      handleTogglePlay();
    }, CLICK_TO_TOGGLE_PLAY_DELAY_MS);
  };

  const handleToggleFullscreen = () => {
    const container = containerRef.current as WebkitFullscreenElement | null;
    if (!container) return;
    const doc = document as WebkitFullscreenDocument;
    // Fullscreen the container, not the <video>, so the zone overlays and
    // this control bar come along.
    const request = getFullscreenElement()
      ? doc.exitFullscreen?.bind(doc) ?? doc.webkitExitFullscreen?.bind(doc)
      : container.requestFullscreen?.bind(container) ??
        container.webkitRequestFullscreen?.bind(container);
    try {
      // Rejects when the browser refuses (e.g. no user gesture); nothing to do.
      Promise.resolve(request?.()).catch(() => undefined);
    } catch {
      // The prefixed API throws instead of rejecting.
    }
  };

  const handleVideoDoubleClick = () => {
    clearClickTimer();
    handleToggleFullscreen();
  };

  React.useEffect(() => {
    const handleFullscreenChange = () => {
      const active =
        getFullscreenElement() !== null &&
        getFullscreenElement() === containerRef.current;
      setIsFullscreen(active);
      if (active) setViewportSize(getViewportSize());
    };
    const handleResize = () => setViewportSize(getViewportSize());
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    window.addEventListener("resize", handleResize);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      window.removeEventListener("resize", handleResize);
      clearClickTimer();
    };
  }, []);

  const handleSeek = (event: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;
    const nextTime = Number(event.target.value);
    video.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  // In fullscreen the container fills the screen, so the overlays have to be
  // laid out against the screen, not the pane size we were given.
  const playerWidth = isFullscreen ? viewportSize.width : width;
  const playerHeight = isFullscreen ? viewportSize.height : height;
  const contentRect = getObjectContainRect(
    playerWidth,
    playerHeight,
    mediaSize.width,
    mediaSize.height
  );
  const markerRatio = getDetectionMarkerRatio({
    durationSeconds: duration,
    approxCaptureTimestamp,
    clipStartTimestamp,
  });

  return (
    <div
      ref={containerRef}
      className="relative bg-black"
      style={{ width: playerWidth, height: playerHeight }}
    >
      <video
        key={videoKey}
        ref={videoRef}
        className="block h-full w-full object-contain"
        autoPlay
        loop
        preload="auto"
        onClick={handleVideoClick}
        onDoubleClick={handleVideoDoubleClick}
        onPlay={syncFromVideo}
        onPause={syncFromVideo}
        onTimeUpdate={syncFromVideo}
        onLoadedMetadata={syncFromVideo}
        onDurationChange={syncFromVideo}
      >
        <source src={src} type="video/mp4" />
      </video>
      <AlertClipOverlays
        contentRect={contentRect}
        ignoreZones={ignoreZones}
        lineCrossings={lineCrossings}
        showIgnoreZones={showIgnoreZones}
        showLineCrossings={showLineCrossings}
      />
      <div className="absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/80 to-transparent px-2 pb-1.5 pt-6">
        <div className="relative mb-1 h-2">
          <input
            type="range"
            min={0}
            max={duration || 0}
            step={0.05}
            value={Math.min(currentTime, duration || 0)}
            onChange={handleSeek}
            aria-label="Clip progress"
            className="absolute inset-0 z-10 h-2 w-full cursor-pointer appearance-none bg-transparent"
          />
          <div className="pointer-events-none absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded bg-white/35" />
          {markerRatio !== null && (
            <div
              className="pointer-events-none absolute top-1/2 z-20 h-2 w-0.5 -translate-y-1/2 bg-actuate-orange"
              style={{ left: `${markerRatio * 100}%` }}
              aria-hidden
            />
          )}
        </div>
        <div className="flex items-center gap-2 text-white">
          <button
            type="button"
            onClick={handleTogglePlay}
            aria-label={isPlaying ? "Pause" : "Play"}
            className="flex h-7 w-7 items-center justify-center"
          >
            {isPlaying ? (
              <PauseIcon fontSize="small" />
            ) : (
              <PlayArrowIcon fontSize="small" />
            )}
          </button>
          <span className="font-mulish text-[11px] tabular-nums">
            {formatPlayerTime(currentTime)} / {formatPlayerTime(duration)}
          </span>
          {onDownload && (
            <button
              type="button"
              onClick={onDownload}
              disabled={!canDownload}
              aria-label="Download video"
              title="Download video"
              className="ml-auto flex h-7 w-7 items-center justify-center disabled:cursor-default disabled:opacity-40"
            >
              <DownloadIcon fontSize="small" />
            </button>
          )}
          <button
            type="button"
            onClick={handleToggleFullscreen}
            aria-label={isFullscreen ? "Exit full screen" : "Full screen"}
            title={isFullscreen ? "Exit full screen" : "Full screen"}
            className={`${
              onDownload ? "" : "ml-auto "
            }flex h-7 w-7 items-center justify-center`}
          >
            {isFullscreen ? (
              <FullscreenExitIcon fontSize="small" />
            ) : (
              <FullscreenIcon fontSize="small" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
